import { use, useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../lib/globals';
import {
  getColorByPlanName,
  getPriceOrContactUs,
  getTestProps,
} from '../../lib/helpers';

import Heading from '../../components/Heading/Heading';
import Switch from '../../components/Switch/Switch';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { useAppForm } from '../../form-hooks/form';
import SpaceTypeCard from '../../pages/SpaceModify/SpaceTypeCard/SpaceTypeCard';
import SpaceSaveButton from './SpaceSaveButton/SpaceSaveButton';

const getInitialValues = (space) => {
  return {
    name: space?.name || '',
    planId: space?.planLimits?.id || '',
    planName: space?.planLimits?.name || '',
    planVisibleName: space?.planLimits?.visible_name || '',
    isFreePlan: space?.planLimits?.price === 0 || false,
    planPrice: space?.planLimits?.price,
    users: space?.users?.map((user) => user.id) || [],
    paymentInterval: space?.paymentInterval || 'month',
  };
};

const SpaceForm = ({
  mode,
  space,
  onSubmit = EMPTY_FUNCTION,
  userOptions = EMPTY_ARRAY,
  usersAreLoading = false,
  spacesFreeCount,
  allPlans,
  disabled,
  testId,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const modal = useModals();
  const { userStorage } = use(UserContext);

  const freeSpaceLimit = userStorage?.data?.limits_plan?.free_space_limit;
  const [allExpanded, toggleExanded] = useReducer(
    (allExpanded) => !allExpanded,
    false,
  );

  const handleUpgrade = useCallback(() => {
    navigate(`/space/upgrade/${space?.id}`);
  }, [navigate, space?.id]);

  const usersFieldListeners = useMemo(
    () => ({
      onChange: ({ value, fieldApi }) => {
        const limit = space?.planLimits?.team_members_limit || 1;
        const newValue = value ? [...value] : [];

        if (limit !== -1 && newValue.length > limit) {
          modal
            .confirmation(
              t('UserForm.TeamMembersLimitExceeded'),
              '',
              t('Spaces.UpgradeSpace'),
            )
            .then((result) => {
              if (result) handleUpgrade();
            });

          newValue.pop();
          fieldApi.handleChange(newValue);
        }
      },
    }),
    [handleUpgrade, modal, space?.planLimits?.team_members_limit, t],
  );

  const validationSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t('Form.FormErrorNotBlank')),
        planId: z
          .string()
          .min(1, t('Spaces.Form.FormErrorSpaceTypeNotSelected')),
        users: z.array(z.string()),
      }),
    [t],
  );

  const onFormSubmit = useCallback(
    async ({ value, formApi }) => {
      const newValues = { ...value };
      if (newValues.users?.length) {
        const newUsers = value.users.map((id) => ({ id }));
        newValues.users = newUsers;
      }

      const [[, errors], hasErrors] = await onSubmit(newValues);

      if (hasErrors) {
        setFormSubmitErrors(formApi, errors);
      } else {
        formApi.reset(value);
      }
    },
    [onSubmit],
  );

  const form = useAppForm({
    defaultValues: getInitialValues(space),
    onSubmit: onFormSubmit,
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSelected = useCallback(
    (plan) => {
      form.setFieldValue('planId', plan.id);
      form.setFieldValue('planName', plan.name);
      form.setFieldValue('planVisibleName', plan.visibleName);
      form.setFieldValue('isFreePlan', plan.price === 0);
      form.setFieldValue('planPrice', plan.price);

      form.validateField('planId');
    },
    [form],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const onPaymentIntervalChange = useCallback(() => {
    form.setFieldValue(
      'paymentInterval',
      form.getFieldValue('paymentInterval') === 'month' ? 'year' : 'month',
    );
  }, [form]);

  return (
    <form.AppForm>
      <form
        id="space-form"
        className="w-full"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        {mode === 'add' && (
          <Heading
            level={4}
            additionalClasses="py-2 font-normal text-xl text-md-2xl dark:text-white"
          >
            {t('Spaces.ChooseAName')}
          </Heading>
        )}
        {mode !== 'upgrade' && (
          <form.AppField name="name">
            {(field) => (
              <field.Input
                label={t('Global.Name')}
                additionalClasses={twMerge(
                  'max-w-3xl',
                  mode === 'edit' && 'mt-4 mb-6',
                )}
                disabled={disabled}
                required
                {...getTestProps(testId, 'space-name', 'testId')}
              />
            )}
          </form.AppField>
        )}

        {mode === 'edit' && (
          <form.AppField
            name="users"
            mode="array"
            listeners={usersFieldListeners}
          >
            {(field) => (
              <field.Dropdown
                options={userOptions}
                isDataLoading={usersAreLoading}
                placeholder={t('Spaces.ChooseUsers')}
                label={t('Spaces.AddUsers', {
                  value: field.state.value.length || 0,
                  limit:
                    space?.planLimits?.team_members_limit === -1
                      ? t('Global.Unlimited')
                      : space?.planLimits?.team_members_limit,
                })}
                disabled={disabled}
                additionalClasses="max-w-3xl break-all md:break-normal"
                maxVisibleValues={5}
                ignoreNotFound={usersAreLoading}
                nullable
                multiple
                {...getTestProps(testId, 'users', 'testId')}
              />
            )}
          </form.AppField>
        )}

        {mode !== 'edit' && (
          <div className="flex flex-col w-full justify-center max-w-screen-2xl mb-5">
            <Heading level={4} additionalClasses="font-normal dark:text-white">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <span
                  className="text-xl text-md-2xl"
                  {...getTestProps(testId, 'text-choose-type-space')}
                >
                  {t('Spaces.ChooseTheSpaceType')}
                </span>
                <div className="flex flex-row">
                  <span
                    className="font-medium text-base text-md-lg mr-2"
                    {...getTestProps(testId, 'switch-label-monthly')}
                  >
                    {t('Spaces.PayMonthly')}
                  </span>

                  <form.AppField name="paymentInterval">
                    {(field) => (
                      <Switch
                        checked={field.state.value === 'year'}
                        onChange={onPaymentIntervalChange}
                        {...getTestProps(testId, 'switch-period', 'testId')}
                      />
                    )}
                  </form.AppField>

                  <span
                    className="font-medium text-base text-md-lg ml-2"
                    {...getTestProps(testId, 'switch-label-yearly')}
                  >
                    {t('Spaces.PayAnnually')}
                  </span>
                </div>
              </div>
            </Heading>

            <form.Subscribe
              selector={({ values }) => ({
                paymentInterval: values.paymentInterval,
                planId: values.planId,
              })}
            >
              {({ paymentInterval, planId }) => (
                <div
                  className={twMerge(
                    'grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4',
                    'gap-5 xl:gap-10 my-6 rounded border border-transparent',
                  )}
                >
                  {allPlans?.map((plan) => (
                    <SpaceTypeCard
                      key={plan.id}
                      name={plan.visibleName}
                      color={getColorByPlanName(plan.visibleName || plan.name)}
                      price={getPriceOrContactUs(
                        paymentInterval === 'year'
                          ? plan.priceYearly
                          : plan.price,
                        t('AccountSettings.ContactUs'),
                        'month',
                      )}
                      limit={
                        plan.visibleName === 'Free'
                          ? `${spacesFreeCount}/${freeSpaceLimit}`
                          : ''
                      }
                      plan={plan}
                      selected={planId === plan.id}
                      disabled={
                        mode === 'add' &&
                        plan.visibleName === 'Free' &&
                        spacesFreeCount?.data === freeSpaceLimit
                      }
                      handleSelected={handleSelected}
                      isCurrentPlan={
                        space?.planLimits?.visible_name === plan.visibleName
                      }
                      isExpanded={allExpanded}
                      {...getTestProps(
                        testId,
                        `type-card-${plan.id}`,
                        'testId',
                      )}
                    />
                  ))}
                </div>
              )}
            </form.Subscribe>

            <span
              className="block mx-auto my-2 py-2 px-5 border border-blue text-blue
                          cursor-pointer rounded-lg transition duration-200 ease-in-out hover:bg-blue hover:text-white"
              onClick={toggleExanded}
            >
              {allExpanded
                ? t('Spaces.ShowLessPlanDetails')
                : t('Spaces.ViewAllPlanDetails')}
            </span>

            {/**
             * There are problems that main validation has no actual data of this field,
             * due to the fact that it is set programmatically.
             */}
            <form.AppField name="planId">
              {(subField) => (
                <subField.ErrorField
                  {...getTestProps(testId, 'space-type-error', 'testId')}
                />
              )}
            </form.AppField>

            <form.Subscribe selector={({ values }) => values}>
              {(values) => (
                <SpaceSaveButton
                  mode={mode}
                  disabled={disabled}
                  space={space}
                  values={values}
                  testId={testId}
                />
              )}
            </form.Subscribe>
          </div>
        )}

        <form.DirtyHandler />
      </form>
    </form.AppForm>
  );
};

export default SpaceForm;
